import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaUpload, FaPlus, FaTrash, FaInfoCircle, FaUserTie } from "react-icons/fa";
import axios from "axios";

const AdminCreateVisaApplication = () => {
  const navigate = useNavigate();
  const url = import.meta.env.VITE_URL || "http://localhost:3000";

  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [visas, setVisas] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedVisaType, setSelectedVisaType] = useState("");
  const [visaPrice, setVisaPrice] = useState(0);

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredUsers = users.filter((u) => {
    const searchString = `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase();
    return searchString.includes(userSearchTerm.toLowerCase());
  });
  useEffect(() => {
    fetchUsers();
    fetchVisas();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${url}/api/user?limit=1000`, { withCredentials: true });
      if (res.data.success) {
        setUsers(res.data.data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    }
  };

  const fetchVisas = async () => {
    try {
      const res = await axios.get(`${url}/api/admin/visa-info`);
      if (res.data.success) {
        setVisas(res.data.visas);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load visas");
    }
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    setSelectedCountry(countryName);
    setSelectedVisaType("");
    setVisaPrice(0);
  };

  const handleVisaTypeChange = (e) => {
    const vType = e.target.value;
    setSelectedVisaType(vType);

    // Find price
    const countryData = visas.find(v => v.country === selectedCountry);
    if (countryData && countryData.visaType) {
      const typeData = countryData.visaType.find(t => t.name === vType || t.type === vType);
      if (typeData && typeData.price) {
        setVisaPrice(String(typeData.price).replace(/[^0-9]/g, ''));
      }
    }
  };

  const [primaryApplicant, setPrimaryApplicant] = useState({
    fullName: "",
    passportNumber: "",
  });

  const currentCountryData = visas.find(v => v.country === selectedCountry);
  const currentTypeData = currentCountryData?.visaType?.find(t => t.name === selectedVisaType || t.type === selectedVisaType);
  const requiredDocsList = currentTypeData?.requiredDocuments && currentTypeData.requiredDocuments.length > 0 
    ? currentTypeData.requiredDocuments 
    : ["Passport", "Photograph", "Proof of Funds"]; // fallback

  const [familyMembers, setFamilyMembers] = useState([]);
  const [documents, setDocuments] = useState({});

  const handleDocChange = (e, docName) => {
    if (e.target.files && e.target.files[0]) {
      setDocuments({ ...documents, [docName]: e.target.files[0] });
    }
  };

  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, {
      fullName: "",
      passportNumber: "",
      documents: {}
    }]);
  };

  const handleFamilyDocChange = (index, e, docName) => {
    if (e.target.files && e.target.files[0]) {
      const updated = [...familyMembers];
      if (!updated[index].documents) updated[index].documents = {};
      updated[index].documents[docName] = e.target.files[0];
      setFamilyMembers(updated);
    }
  };

  const removeFamilyMember = (index) => {
    const updated = familyMembers.filter((_, i) => i !== index);
    setFamilyMembers(updated);
  };

  const handleFamilyChange = (index, field, value) => {
    const updated = [...familyMembers];
    updated[index][field] = value;
    setFamilyMembers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!primaryApplicant.fullName || !primaryApplicant.passportNumber) {
      toast.error("Please fill primary applicant details");
      return;
    }

    // Check required docs dynamically based on visa type
    for (let req of requiredDocsList) {
      if (!documents[req]) {
        toast.error(`Please upload ${req}`);
        return;
      }
    }

    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    if (!selectedCountry || !selectedVisaType) {
      toast.error("Please select country and visa type");
      return;
    }

    const formData = new FormData();
    formData.append("userId", selectedUser);
    formData.append("country", selectedCountry);
    formData.append("visaType", selectedVisaType);
    formData.append("price", visaPrice);
    formData.append("primaryFullName", primaryApplicant.fullName);
    formData.append("primaryPassportNumber", primaryApplicant.passportNumber);
    formData.append("familyMembers", JSON.stringify(familyMembers));

    Object.keys(documents).forEach((key) => {
      if (documents[key]) {
        formData.append(key, documents[key]);
      }
    });

    familyMembers.forEach((member, index) => {
      if (member.documents) {
        Object.keys(member.documents).forEach(docKey => {
          if (member.documents[docKey]) {
            formData.append(`familyMember_${index}_${docKey}`, member.documents[docKey]);
          }
        });
      }
    });

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const url = import.meta.env.VITE_URL || "http://localhost:3000";
      const response = await fetch(`${url}/api/visa/admin/apply`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        toast.success("Visa application created on behalf of user!");
        navigate("/admin/visa-applications");
      } else {
        toast.error(data.message || "Failed to submit application");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const FileUploadBox = ({ title, docKey, required = false, accept = ".jpg,.jpeg,.png,.pdf" }) => (
    <div className="border border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition-colors">
      <h4 className="text-sm font-semibold mb-1 text-gray-800">
        {title} {required && <span className="text-red-500">*</span>}
      </h4>
      <p className="text-xs text-gray-500 mb-4">Accepted: {accept}</p>

      <label className="cursor-pointer flex flex-col items-center">
        <div className="w-10 h-10 bg-white shadow flex items-center justify-center rounded-full mb-2 text-blue-500">
          <FaUpload />
        </div>
        <span className="text-sm font-medium text-blue-600 hover:underline">
          {documents[docKey] ? documents[docKey].name : "Click to upload"}
        </span>
        <input
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => handleDocChange(e, docKey)}
        />
      </label>
    </div>
  );

  const FamilyFileUploadBox = ({ title, docKey, index, required = false, accept = ".jpg,.jpeg,.png,.pdf" }) => {
    const file = familyMembers[index]?.documents?.[docKey];
    return (
      <div className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-center bg-white hover:bg-gray-50 transition-colors">
        <h4 className="text-xs font-semibold mb-1 text-gray-800">
          {title} {required && <span className="text-red-500">*</span>}
        </h4>
        <label className="cursor-pointer flex flex-col items-center mt-2">
          <div className="w-8 h-8 bg-gray-100 flex items-center justify-center rounded-full mb-1 text-blue-500">
            <FaUpload size={12} />
          </div>
          <span className="text-xs font-medium text-blue-600 hover:underline truncate max-w-[120px]">
            {file ? file.name : "Upload"}
          </span>
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={(e) => handleFamilyDocChange(index, e, docKey)}
          />
        </label>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-blue-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate("/admin/visa-applications")} className="text-blue-600 mb-4 font-medium flex items-center gap-2">
          &lt; Back to Applications
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FaUserTie className="text-blue-600" />
            Create Application for User
          </h1>
          <p className="text-gray-600">Apply for a visa on behalf of a user. The application will be automatically approved.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
              <span className="bg-blue-600 text-white w-6 h-6 flex justify-center items-center rounded-full text-sm">1</span>
              User & Visa Selection
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              <div ref={dropdownRef} className="relative">
                <label className="block text-sm text-gray-600 mb-1">Select User <span className="text-red-500">*</span></label>
                
                <div 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 bg-white cursor-pointer flex justify-between items-center"
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                >
                  <span className={selectedUser ? "text-gray-900" : "text-gray-500"}>
                    {selectedUser 
                      ? (() => {
                          const u = users.find(u => u._id === selectedUser);
                          if (!u) return "Select User";
                          const nameParts = [u.firstName, u.lastName].filter(Boolean).join(" ");
                          return nameParts ? `${nameParts} (${u.email})` : `(${u.email})`;
                        })()
                      : "-- Choose User --"
                    }
                  </span>
                  <span className="text-gray-400 text-xs">▼</span>
                </div>

                {isUserDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 flex flex-col">
                    <div className="p-2 border-b border-gray-200">
                      <input 
                        type="text" 
                        placeholder="Search by name or email..." 
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    <div className="overflow-y-auto">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map(u => {
                          const nameParts = [u.firstName, u.lastName].filter(Boolean).join(" ");
                          return (
                            <div 
                              key={u._id} 
                              className={`px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm ${selectedUser === u._id ? "bg-blue-100 font-medium text-blue-700" : "text-gray-700"}`}
                              onClick={() => {
                                setSelectedUser(u._id);
                                setIsUserDropdownOpen(false);
                                setUserSearchTerm("");
                              }}
                            >
                              {nameParts ? `${nameParts} ` : ''}<span className="text-gray-500">({u.email})</span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No users found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Select Country <span className="text-red-500">*</span></label>
                <select
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  required
                >
                  <option value="">-- Choose Country --</option>
                  {visas.map(v => (
                    <option key={v._id} value={v.country}>{v.country}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Select Visa Type <span className="text-red-500">*</span></label>
                <select
                  value={selectedVisaType}
                  onChange={handleVisaTypeChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  required
                  disabled={!selectedCountry}
                >
                  <option value="">-- Choose Visa Type --</option>
                  {visas.find(v => v.country === selectedCountry)?.visaType?.map((t, idx) => (
                    <option key={idx} value={t.name || t.type}>{t.name || t.type} (₹{t.price})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 border-y border-gray-200 mt-4">
            <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
              <span className="bg-blue-600 text-white w-6 h-6 flex justify-center items-center rounded-full text-sm">2</span>
              Primary Applicant
            </h2>
          </div>

          <div className="p-6">
            <h3 className="font-semibold mb-4 text-gray-700">Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Full Name (as per Passport) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={primaryApplicant.fullName}
                  onChange={(e) => setPrimaryApplicant({ ...primaryApplicant, fullName: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Passport Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={primaryApplicant.passportNumber}
                  onChange={(e) => setPrimaryApplicant({ ...primaryApplicant, passportNumber: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="A1234567"
                  required
                />
              </div>
            </div>

            <h3 className="font-semibold mb-4 text-gray-700">Required Documents</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {requiredDocsList.map(doc => (
                <FileUploadBox key={doc} title={doc} docKey={doc} required />
              ))}
            </div>

            {familyMembers.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold mb-4 text-gray-700">Family Members</h3>
                {familyMembers.map((member, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-4 relative">
                    <button type="button" onClick={() => removeFamilyMember(idx)} className="absolute top-4 right-4 text-red-500 hover:text-red-700">
                      <FaTrash />
                    </button>
                    <h4 className="font-medium text-sm mb-3">Family Member {idx + 1}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Full Name</label>
                        <input
                          type="text"
                          value={member.fullName}
                          onChange={(e) => handleFamilyChange(idx, 'fullName', e.target.value.toUpperCase())}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Passport Number</label>
                        <input
                          type="text"
                          value={member.passportNumber}
                          onChange={(e) => handleFamilyChange(idx, 'passportNumber', e.target.value.toUpperCase())}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="mt-4 border-t pt-4">
                      <h5 className="text-xs font-semibold text-gray-700 mb-3">Family Member Documents</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {requiredDocsList.map(doc => (
                          <FamilyFileUploadBox key={doc} index={idx} title={doc} docKey={doc} required />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center mb-8">
              <button
                type="button"
                onClick={addFamilyMember}
                className="flex items-center gap-2 px-6 py-2 bg-blue-50 text-blue-600 rounded-full font-medium hover:bg-blue-100 transition-colors"
              >
                <FaPlus size={12} /> Add Family Member
              </button>
            </div>

          </div>

          <div className="bg-gray-50 px-6 py-5 border-t border-gray-200">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-blue-200 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? "Submitting Application..." : "Submit Visa Application"}
            </button>
            <p className="text-center text-xs text-gray-500 mt-3">
              By submitting this form, you agree to our Terms of Service and Privacy Policy. Your documents are securely encrypted.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCreateVisaApplication;
